import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import SlideOutSidebar from "../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { ButtonSizes, ButtonThemes, SmallButton } from "../../../../ui-components/button/button";
import { ShipmentWorkItemAccessorialsState } from "../../../../slices/shipment-reconciliation/ShipmentReconciliationState";
import {
  getShipmentWorkItemAccessorials,
  shipmentWorkItemAccessorialsSelector
} from "../../../../slices/shipment-reconciliation/shipmentWorkItemAccessorialsSlice";
import {
  AccessorialApprovalStatus,
  ShipmentWorkItemAccessorial,
  ShipmentWorkItemReviewStatus
} from "../../../../app/data/shipment-reconciliation/models";
import ShipmentDetailsState from "../../../../slices/shipment-details/ShipmentDetailsState";
import {
  getShipmentDetails,
  resetShipmentDetails,
  shipmentDetailsSelector
} from "../../../../slices/shipment-details/shipmentDetailsSlice";
import ShipmentDetailsModel from "../../../../app/data/tracking/ShipmentDetailsModel";
import InvoiceDetailsInfo from "../../../invoices/invoice-details/info/invoiceDetailsInfo";
import InvoiceDetailsDocuments from "../../../invoices/invoice-details/documents/invoiceDetailsDocuments";
import {
  changeOperationsReviewListLocally,
  getOperationsReviewListSelector
} from "../../../../slices/shipment-reconciliation/operations-review/getOperationsReviewListSlice";
import {
  changeOperationsReviewSummaryLocally,
  getOperationsReviewSummarySelector
} from "../../../../slices/shipment-reconciliation/operations-review/getOperationsReviewSummarySlice";
import { OperationsReviewListState, OperationsReviewSummaryState } from "../../../../slices/shipment-reconciliation/ShipmentReconciliationState";
import AccessorialsReviewSection from "../../shared/accessorials-review/AccessorialsReviewSection";
import "./operationReviewItemDetailsV2.scss";

interface OperationsReviewItemDetailsV2Props {
  shipmentWorkItemId: number;
  probillNumber: number;
  terminalAirportCode: string;
  terminalName: string;
  terminalNumber: number;
  onClose: () => void;
}

const OperationsReviewItemDetailsV2: React.FC<OperationsReviewItemDetailsV2Props> = memo((props) => {
  const dispatch = useDispatch();
  const shipmentDetailsState: ShipmentDetailsState = useSelector(shipmentDetailsSelector);
  const shipmentWorkItemAccessorials: ShipmentWorkItemAccessorialsState = useSelector(shipmentWorkItemAccessorialsSelector);
  const operationsReviewList: OperationsReviewListState = useSelector(getOperationsReviewListSelector);
  const operationsReviewSummary: OperationsReviewSummaryState = useSelector(getOperationsReviewSummarySelector);

  const [showInvoiceDocuments, setShowInvoiceDocuments] = useState(false);

  const shipment = shipmentDetailsState.shipment as ShipmentDetailsModel | null;

  useEffect(() => {
    dispatch(getShipmentWorkItemAccessorials(props.shipmentWorkItemId, { isOperationsReviewRequired: true }, (error) => toast.error(error)));
  }, [dispatch, props.shipmentWorkItemId]);

  useEffect(() => {
    if (!props.probillNumber) return;
    dispatch(getShipmentDetails(props.probillNumber));
    return () => {
      dispatch(resetShipmentDetails());
    };
  }, [dispatch, props.probillNumber]);

  const tagLabels = useMemo(() => {
    if (shipmentWorkItemAccessorials.loading) return [];
    const names = (shipmentWorkItemAccessorials.accessorials || []).map((a) => a.accessorialDefinition?.name).filter(Boolean);
    return [...new Set(names as string[])].slice(0, 6);
  }, [shipmentWorkItemAccessorials.accessorials, shipmentWorkItemAccessorials.loading]);

  const adjustOperationsReviewSummaryAndList = useCallback(
    (accessorialId: string, approvalStatus: AccessorialApprovalStatus, accessorialRate: string) => {
      const currentReviewSummary = { ...operationsReviewSummary.summary };
      const currentReviewList = [...operationsReviewList.shipmentWorkItems];
      const currentAccessorialsList = [...shipmentWorkItemAccessorials.accessorials];

      const affectedWorkItemIndex = currentReviewList.findIndex((ele) => ele.id === props.shipmentWorkItemId);
      if (affectedWorkItemIndex === -1) return;

      const affectedWorkItem = { ...currentReviewList[affectedWorkItemIndex] };

      let isReviewComplete = true;
      if (currentAccessorialsList?.length) {
        currentAccessorialsList.forEach((accessorial) => {
          if (accessorial && accessorial.id !== accessorialId && accessorial.operationApprovalStatus === AccessorialApprovalStatus.PENDING) {
            isReviewComplete = false;
          }
        });
      }

      if (isReviewComplete) {
        if (affectedWorkItem.operationsReviewStatus !== ShipmentWorkItemReviewStatus.REVIEWED) {
          currentReviewSummary.compltedOperationsReview += 1;
          currentReviewSummary.pendingAppliedRate -= +accessorialRate;
          currentReviewSummary.pendingOperationsReview -= 1;
          if (approvalStatus === AccessorialApprovalStatus.ACCEPTED) {
            currentReviewSummary.acceptedAppliedRate += +accessorialRate;
          } else {
            currentReviewSummary.rejectedAppliedRate += +accessorialRate;
          }
          affectedWorkItem.operationsReviewStatus = ShipmentWorkItemReviewStatus.REVIEWED;
        }
      } else if (affectedWorkItem.operationsReviewStatus !== ShipmentWorkItemReviewStatus.REVIEWED) {
        currentReviewSummary.compltedOperationsReview += 1;
        currentReviewSummary.pendingAppliedRate -= +accessorialRate;
        if (approvalStatus === AccessorialApprovalStatus.ACCEPTED) {
          currentReviewSummary.acceptedAppliedRate += +accessorialRate;
        } else {
          currentReviewSummary.rejectedAppliedRate += +accessorialRate;
        }
      }

      const affectedAccessorials: ShipmentWorkItemAccessorial[] = [];
      if (affectedWorkItem.accessorials?.length) {
        affectedWorkItem.accessorials.forEach((accessorial: ShipmentWorkItemAccessorial) => {
          if (!accessorial) return;
          const affectedAccessorial = { ...accessorial };
          if (accessorial.id === accessorialId) {
            affectedAccessorial.operationApprovalStatus = approvalStatus;
          }
          affectedAccessorials.push(affectedAccessorial);
        });
      }
      affectedWorkItem.accessorials = affectedAccessorials;
      currentReviewList[affectedWorkItemIndex] = affectedWorkItem;
      dispatch(changeOperationsReviewListLocally({ shipmentWorkItems: currentReviewList }));
      dispatch(changeOperationsReviewSummaryLocally({ summary: currentReviewSummary }));
    },
    [
      dispatch,
      operationsReviewList.shipmentWorkItems,
      operationsReviewSummary.summary,
      props.shipmentWorkItemId,
      shipmentWorkItemAccessorials.accessorials
    ]
  );

  const refetchAccessorials = useCallback(() => {
    dispatch(
      getShipmentWorkItemAccessorials(
        props.shipmentWorkItemId,
        { isOperationsReviewRequired: true },
        (error) => {
          toast.error(error);
        },
        { preserveAccessorials: true }
      )
    );
  }, [dispatch, props.shipmentWorkItemId]);

  const showPartyBlocks = shipment?.fullAccess !== false;

  return (
    <>
      <SlideOutSidebar
        className="xgs-operations-review-v2-sidebar"
        header="Operations Review"
        onClose={props.onClose}
        size="medium"
        show={!!props.shipmentWorkItemId}
      >
        <div className="xgs-operations-review-item-details-v2">
          <Link
            className="xgs-operations-review-item-details-v2__probill xgs-operations-review-item-details-v2__probill-link"
            to={`/shipments/${props.probillNumber}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {`Probill #${props.probillNumber}`}
          </Link>

          {tagLabels.length > 0 && (
            <div className="xgs-operations-review-item-details-v2__tags">
              {tagLabels.map((t) => (
                <span key={t} className="xgs-operations-review-item-details-v2__tag">
                  {t}
                </span>
              ))}
            </div>
          )}

          {shipmentDetailsState.loading && !shipmentDetailsState.loaded && (
            <div className="xgs-operations-review-item-details-v2__loading">Loading shipment details…</div>
          )}
          {shipmentDetailsState.loadingFailed && (
            <div className="xgs-operations-review-item-details-v2__loading">Shipment details could not be loaded. Party information may be unavailable.</div>
          )}

          <hr className="xgs-operations-review-item-details-v2__divider" />

          {showPartyBlocks && shipment && (
            <>
              <div className="xgs-operations-review-item-details-v2__invoice-parties">
                <InvoiceDetailsInfo {...shipment} partiesOnly linkPartyAddressesToGoogleMaps />
              </div>
              <div className="xgs-operations-review-item-details-v2__bol-row">
                <SmallButton theme={ButtonThemes.blue} size={ButtonSizes.small} type="button" onClick={() => setShowInvoiceDocuments(true)}>
                  View BOL
                </SmallButton>
                <SmallButton theme={ButtonThemes.blue} size={ButtonSizes.small} type="button" onClick={() => setShowInvoiceDocuments(true)}>
                  View POD
                </SmallButton>
              </div>
            </>
          )}

          <hr className="xgs-operations-review-item-details-v2__divider" />

          <AccessorialsReviewSection
            accessorials={shipmentWorkItemAccessorials.accessorials || []}
            loading={shipmentWorkItemAccessorials.loading}
            shipmentWorkItemId={props.shipmentWorkItemId}
            terminalAirportCode={props.terminalAirportCode}
            terminalName={props.terminalName}
            terminalNumber={props.terminalNumber}
            probillNumber={props.probillNumber}
            shipmentWeight={shipment?.weight}
            onAfterSubmit={adjustOperationsReviewSummaryAndList}
            onRefetch={refetchAccessorials}
          />
        </div>
      </SlideOutSidebar>

      <SlideOutSidebar
        header="Shipment documents"
        onClose={() => setShowInvoiceDocuments(false)}
        show={showInvoiceDocuments}
        size="small-605"
        spinner={false}
        className="xgs-operations-review-v2-documents-sidebar xgs-slide-out-sidebar--stack-above-page-modals"
      >
        <InvoiceDetailsDocuments invoiceNumber={props.probillNumber} />
      </SlideOutSidebar>
    </>
  );
});

export default OperationsReviewItemDetailsV2;
